import React, {useState, useContext, Fragment, useEffect} from "react";

import { FormattedMessage } from "react-intl";
import { NotificationsContext } from "../ui-components/Notifications";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import Button from "../ui-components/Button";
import { PageHeading } from "../ui-components/Container";
import ErrorBoundary from "../ui-components/ErrorBoundary";
import DbHandlerTable from "./DbModelTable";
import DbModelCreateRow from "./DbModelCreateRow";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";

const DbModelHandler = ({
  table: {
    name,
    path,
    columns,
    publicTable,
    staticTable,
    customAddOnClick,
    parentField,
    parentDescriptionField,
    fieldsLockedByParent,
    dynamicLookups,
    editable=true,
    deletable=true,
    getCustomDelete,
    actions,
  },
  data,
  setData,
}) => {

  // context
  const { push } = useContext(NotificationsContext);

  // state
  const [lookups, setLookups] = useState({});
  const [showNewRowDialog, setShowNewRoleDialog] = useState(false);
  const [selectedParent, setSelectedParent] = useState();
  const [submitLoading, setSubmitLoading] = useState(false);

  // hooks
  const countryApiRequest = useCountryAxiosInstance();

  // utilities
  const columnsExt = columns.map((c) => c.field in lookups
    ? {...c, lookup: lookups[c.field]}
    : c
  );

  useEffect(() => {
    if (!dynamicLookups)
      return;

    Object.entries(dynamicLookups).forEach(([field, {url, kpGenerator, optional}]) => {
      countryApiRequest.get(url)
        .then((result) => setLookups((prev) => ({
          ...prev,
          [field]: {
            ...(optional ? {[null]: 'Nessuno'} : {}),
            ...result.reduce((prev, curr) => ({...prev, ...kpGenerator(curr)}), {})
          }
        })))
        .catch((error) => {
          push({type: 'error', title: 'server_error'});
          console.error(error);
        })
    });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!parentField)
      return;
    setShowNewRoleDialog(!!selectedParent);
  }, [selectedParent]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!parentField || !!showNewRowDialog)
      return;
    setSelectedParent(null);
  }, [showNewRowDialog]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Fragment>
      <PageHeading
        title={name}
        contentActions={staticTable ? null :
          <Button
            variant="contained"
            color="primary"
            submitting={submitLoading}
            onClick={async () => {
              if(customAddOnClick){
                await customAddOnClick(push, countryApiRequest, setData, setSubmitLoading);
              } else {
                setShowNewRoleDialog(true)
              }
            }}
          >
            <PlusCircleIcon
              className="-ml-0.5 mr-2 h-4 w-4"
              aria-hidden="true"
            />
            <FormattedMessage id="add" />
          </Button>
        }
      />
      <ErrorBoundary from="table">
        {data ? (
          <DbHandlerTable
            rows={data}
            setRows={setData}
            push={push}
            apiKey={path}
            columns={columnsExt}
            staticTable={staticTable}
            setSelectedParent={setSelectedParent}
            parentField={parentField}
            setData={setData}
            editable={editable}
            deletable={deletable}
            getCustomDelete={getCustomDelete}
            actions={actions}
          />
        ) : null}
      </ErrorBoundary>
      <ErrorBoundary from="table-add-dialog">
        {showNewRowDialog ? (
          <DbModelCreateRow
            open={showNewRowDialog}
            toggleOpen={setShowNewRoleDialog}
            data={data}
            setData={setData}
            columns={columnsExt}
            apiKey={path}
            publicTable={publicTable}
            selectedParent={selectedParent}
            setSelectedParent={setSelectedParent}
            parentField={parentField}
            parentDescriptionField={parentDescriptionField}
            fieldsLockedByParent={fieldsLockedByParent}
          />
        ) : null}
      </ErrorBoundary>
    </Fragment>
  );
};

export default DbModelHandler;
